import React, { useState, useEffect } from 'react';
import { Stack } from 'new/elements/Stack';
import Typography from 'new/elements/TypographySupport/TypographySupport';
import { Divider } from '@mui/material';
import { API_SYSLOG_SERVER_URL } from 'js/constants/api';
import CodeContainer from 'js/components/data_sources/add_ds_form/SyslogConfiguration/SyslogCodeContainer';
import { useSelector } from 'react-redux';
import { getAccessToken } from 'js/reducers/session/selectors';

function getCurl(syslogServerUrl, payload, token) {
  return `curl --location --request POST ${syslogServerUrl} \
--header 'Content-Type: application/json' \
--header 'Authorization: Bearer ${token}' \
--data '${payload}'`;
}

const payloadSchema = (sourceId = '', datasetId = '') => `{
  //..syslog data
}`;

export default function SyslogConfigView(props) {
  const { data } = props;
  const sourceId = data?.id || '';
  const datasetId = data?.data_sets?.[0] || '';
  const [token, setToken] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const accessToken = useSelector(getAccessToken);

  useEffect(() => {
    const fetchToken = async () => {
      if (!sourceId || !datasetId || !accessToken) return;

      setLoading(true);
      setError('');

      try {
        const response = await fetch(
          `/api/fetch_token?source_id=${sourceId}&dataset_id=${datasetId}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error('Failed to fetch token');
        }

        const data = await response.json();
        setToken(data.token);
      } catch (err) {
        setError('Failed to fetch token. Please try again.');
        console.error('Token fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchToken();
  }, [sourceId, datasetId, accessToken]);

  const syslogServerUrl = API_SYSLOG_SERVER_URL || '';
  const payload = payloadSchema(sourceId, datasetId);
  const sanitizedToken = token.substring(0, 10) + '**************';

  return (
    <Stack paddingBottom="24px" gap="16px">
      <Typography variant="bodyDefault">
        This configuration page provides everything you need to stream syslog data from your Syslog
        server to Nexla's data integration platform. Below you'll find the endpoint details and
        payload format required to start sending logs to our platform. Once configured, your Syslog
        server can continuously stream data that will be automatically processed and integrated
        through Nexla.
      </Typography>
      <Divider />
      <Typography variant="subtitle2">Endpoint</Typography>
      <Typography variant="bodyDefault">
        Send your syslog data to this endpoint to ingest it into Nexla.
      </Typography>
      <CodeContainer toCopy={`${syslogServerUrl}`} value={`${syslogServerUrl}`} label="" />
      <Typography variant="bodyDefault">
        All requests to this endpoint must be authenticated using Bearer Token authorization
      </Typography>
      {loading ? (
        <CodeContainer toCopy="" value="Loading token..." label="" />
      ) : error ? (
        <CodeContainer toCopy="" value={`Error in fetching token`} label="" />
      ) : !token ? (
        <CodeContainer toCopy="" value="No token available" label="" />
      ) : (
        <CodeContainer
          toCopy={`Authorization: Bearer ${token}`}
          value={`Authorization: Bearer ${sanitizedToken}`}
          label="Authorization Header"
        />
      )}

      <Divider />

      <Typography variant="subtitle2">Payload</Typography>
      <Typography variant="bodyDefault">
        Send your syslog data as JSON payloads to this endpoint. The JSON should contain all the log
        data fields you want to ingest into Nexla.
      </Typography>
      <CodeContainer toCopy={payload} value={payload} label="" />

      <Divider />

      <Typography variant="subtitle2">Sample curl Command</Typography>
      <CodeContainer
        toCopy={getCurl(syslogServerUrl, payload, token)}
        value={getCurl(syslogServerUrl, payload, sanitizedToken)}
        label=""
      />
    </Stack>
  );
}
